import React, { forwardRef,useImperativeHandle, useRef, useState } from "react";
import {  RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCTextArea, RCCSelectOptionsCard} from "react-common-components";
import {  useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import ModalSide from "src/components/Modal/styles";
import DateTimePicker from "src/components/DateTimePicker";

import { IComponentOccurrenceRegisterHistoryModal, IOccurrence, IRefModalRegisterHistory } from "../interface";
import { registerHistorySchema } from "../schemas";

const modalRegisterHistory : React.ForwardRefRenderFunction<IRefModalRegisterHistory,IComponentOccurrenceRegisterHistoryModal> = ({
	callbackUpdate
}, ref) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const modalRegisterIncident = useRef<RCCRefModalProps>(null);
	const [ selectedOccurrence, setSelectedOccurrence] = useState<IOccurrence>({} as IOccurrence);
	const { handleSubmit, control, register, reset, setError, formState: { errors }, clearErrors } = useForm();

	useImperativeHandle(ref, () => ({
		actionButtonRegisterHistory
	}));

	const actionButtonRegisterHistory = (occurence: IOccurrence) => {
		reset();
		queryOccurenceData(occurence.ocb001Id);
	};

	const queryOccurenceData = async (id: number) => {
		setLoading(true);
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Occurrences/Select",
				urlParameters: {
					id
				}
			});

			setSelectedOccurrence(data);

			modalRegisterIncident.current?.openModal();

			setLoading(false);
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao carregar ocorrência"), addToast);
		}
	};


	
	const onSubmit = async (data: any) => {
		try{
			clearErrors();	
			modalRegisterIncident.current?.setLoadingButton();

			const dataChanged = data;
			
			await registerHistorySchema(translate).validate(data,{
				abortEarly: false
			});

			const offset = dataChanged.ocb003DataHora.getTimezoneOffset();
			dataChanged.ocb003DataHora = new Date(dataChanged.ocb003DataHora.getTime() - (offset*60*1000));

			dataChanged.efficiency  = selectedOccurrence.verifyEfficiency;

			await RCCServicesApi.postRequest({
				method: "Occurrences/RegisterHistory",
				body: {
					...dataChanged,
					ocb003Oca003Id: selectedOccurrence.ocb001Oca003Id,
					ocb003Ocb001Id: selectedOccurrence.ocb001Id
				}
			});

			if(callbackUpdate){
				callbackUpdate(selectedOccurrence.ocb001Id);
			}
			

			addToast({
				color: "green",
				description: translate("Registro de histórico de ocorrência inserido com sucesso"),
				title: translate("Sucesso")
			});

			modalRegisterIncident.current?.resetLoadingButton();
			modalRegisterIncident.current?.closeModal();
		}catch(err: any){
			modalRegisterIncident.current?.resetLoadingButton();
			if(getValidationErrors(err, setError))
				return;

			const textMessageError = err?.response?.data?.Message;
			if(textMessageError === "fcaPending"){
				addToast({
					title: translate("Erro"),
					description: translate("Essa ocorrência necessita Análise FCA para ser finalizada"),
					color: "yellow"
				});
			}else if(textMessageError === "actionsPending"){
				addToast({
					title: translate("Erro"),
					description: translate("Para finalizar a ocorrência, é necessário que todas as ações estejam concluídas/canceladas"),
					color: "yellow"
				});
			}else{
				standardErrorMessages(err,  translate("Erro ao registrar histórico de ocorrência"), addToast);
			}
			
			clearErrors();
		}
	};

	return (
		<>
			<Modal
				ref={modalRegisterIncident} 
				header={{
					title: translate("Registro de Histórico"),
					icon: {
						name: "infoCircle"
					},
					subtitle: `${translate("Ocorrência")}: ${selectedOccurrence.fullTitle}`
				}}
				onSubmit={handleSubmit(onSubmit)}
				styleInline={ModalSide}
			>
				<RCCGrid
					item
					container
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						direction="column"
						alignItems="center"
					>
						<RCCGrid
							container
							item
							xs={12}
							sm={12}
						>
							<RCCInput 
								label={translate("Etapa atual")} 
								name="oca003Descricao" 
								defaultValue={translate(selectedOccurrence.oca003Descricao)} 
								disabled
							/>
						</RCCGrid>
						<RCCGrid
							container
							item
							xs={12}
							sm={12}
						>
							<RCCTextArea 
								register={register}
								name="ocb003Comentario"
								label={translate("Comentário")}
								maxLength={4000}
								error={errors.ocb003Comentario}
								required
							/>
						</RCCGrid>
						<RCCGrid
							container
							item
							xs={12}
							sm={12}
						>
							<DateTimePicker 
								type="dateTimePicker"
								control={control}
								label={translate("Data e hora")}
								name="ocb003DataHora"
								defaultValue={new Date()}
								required
							/>
						</RCCGrid>
						{selectedOccurrence.verifyEfficiency === "S" ? 
							(
								<RCCGrid
									container
									item
									xs={12}
									sm={12}
									style={{marginTop: 10}}
								>
									<RCCSelectOptionsCard
										control={control}
										name="ocb003Aprovada"
										defaultValue="S"
										options={[
											{value: "S", label: translate("Aprovada")},
											{value: "N", label: translate("Reprovada")}
										]}
										label={translate("Verificação Eficácia")}
									/>
								</RCCGrid>
							)
							:
							(
								<RCCGrid
									container
									item
									xs={12}
									sm={12}
									style={{marginTop: 10}}
								>
									<RCCSelectOptionsCard
										control={control}
										name="ocb003Aprovada"
										defaultValue="S"
										options={[
											{value: "S", label: translate("Aprovada")},
											{value: "N", label: translate("Reprovada")},
											{value: "R", label: translate("Registro")}
										]}
										label={translate("Situação")}
									/>
								</RCCGrid>
							)
						}
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(modalRegisterHistory);
